import {
  Component,
  OnInit,
  QueryList,
  ElementRef,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PlaceholderChildComponent } from '@ispm/layout/classes/placeholder-child-component.abstract';
import {
    TreatmentPlanData,
    TreatmentPlanSelectorResult
} from '@ispm-client/modules/bpmn-modeler/models/treatment-plan-selector-result';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { icons, PANEL_MODES } from '@ispm-client/modules/bpmn-modeler/constants/constants';
import { NodePanelConfigurationService } from '@ispm-client/modules/bpmn-modeler/service/node-panel-configuration.service';
import { cloneDeep } from 'lodash';
import { Panel } from 'primeng/panel';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { map } from 'rxjs/operators';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { SelectorUrlBuilderService } from '@ispm-client/modules/bpmn-modeler/service/selector-url-builder.service';

@Component({
    selector: 'ispm-treatment-plan-selector',
    templateUrl: './treatment-plan-selector.component.html',
    styleUrls: ['./treatment-plan-selector.component.scss']
})
export class TreatmentPlanSelectorComponent extends PlaceholderChildComponent<any> implements OnInit {

  public readonly PRIMARY_ICON = icons.PRIMARY_ICON;
  public readonly NON_PRIMARY_ICON = icons.NON_PRIMARY_ICON;
  public readonly EXPAND_ICON = icons.EXPAND_ICON;
  public readonly COLLAPSED_ICON = icons.COLLAPSED_ICON;
  public readonly EXPAND_ALL_ICON = icons.EXPAND_ALL_ICON;
  public readonly COLLAPSED_ALL_ICON = icons.COLLAPSED_ALL_ICON;

  data: TreatmentPlanSelectorResult;
  private validationMap = { };
  private revisedEntitiesRevisionsData: any;
  public panelsState: Map<number, PANEL_MODES> = new Map<number, PANEL_MODES>();
  @ViewChildren('panel') private treatmentPlansElements: QueryList<Panel>;
  @ViewChild('treatmentsContainer') private treatmentsScrollContainer: ElementRef;
  public selectedTreatments: boolean[] = [];

  public get selectedTreatmentsAmount(): number {
    return this.selectedTreatments.filter(selected => selected).length;
  }

  public get selectAllTreatments(): boolean {
    return (this.selectedTreatmentsAmount === this.data.treatmentPlans.length);
  }

  public set selectAllTreatments(selected: boolean) {
    this.onSelectAllClick(selected);
  }

  public get selectedCounterText() {
    return (this.selectedTreatmentsAmount > 1) ? `${this.selectedTreatmentsAmount} Plans Selected` : `${this.selectedTreatmentsAmount} Plan Selected`;
  }

  public get allOpen(): boolean {
    return this.treatmentPlansElements &&
           Array.from(this.treatmentPlansElements)
            .filter((element, index) => this.isTreatmentSelected(index))
            .every(element => !element.collapsed);
  }

  public isTreatmentSelected(itemIndex) {
    return this.selectedTreatments[itemIndex];
  }

  public get panelExpandMode() {
    return this.allOpen ? PANEL_MODES.EXPANDED : PANEL_MODES.COLLAPSED;
  }

  public isCollapsed(itemIndex: number) {
    return this.panelsState.get(itemIndex) === PANEL_MODES.COLLAPSED;
  }

  constructor(
    private nodePanelConfigurationService: NodePanelConfigurationService,
    private popupLoader: DynamicModalLoaderService,
    private selectorUrlBuilderService: SelectorUrlBuilderService
  ) {
      super();
  }

  beforeClose() {
  }

  ngOnInit() {
      this.data = cloneDeep(this.payload.data);
      this.nodePanelConfigurationService.storeNodeConfigurationData(cloneDeep(this.payload.data), this.payload.readOnlyMode, true);

      this.revisedEntitiesRevisionsData = cloneDeep(this.payload.revisedEntitiesRevisionsData);
      if (!this.data.treatmentPlans) {
          this.data.treatmentPlans = [];
          this.validationMap = { };
          this.addNewTreatmentPlan();
      } else {
          this.data.treatmentPlans.forEach((treatmentPlanPayLoad, index) => {
              treatmentPlanPayLoad.treatmentPlanGuid = treatmentPlanPayLoad.treatmentPlanGuid || StringUtils.generateGuid();
              this.validationMap[treatmentPlanPayLoad.treatmentPlanGuid] = this.isIDandNameExistedForTreatment(treatmentPlanPayLoad);
              this.panelsState.set(index, PANEL_MODES.COLLAPSED);
              this.selectedTreatments[index] = false;
          });
      }
  }

  public addNewTreatmentPlan(): void {
    const guid = StringUtils.generateGuid();
    this.data.treatmentPlans = [...this.data.treatmentPlans, {
        primary: false,
        treatmentPlan: { },
        treatmentPlanGuid: guid
    }];
    this.selectedTreatments = [...this.selectedTreatments, false];
    this.panelsState.set(this.selectedTreatments.length - 1, PANEL_MODES.EXPANDED);
    this.validationMap[guid] = false;
    this.scrollToBottom();
  }

  public removeTreatmentPlan(index): void {
      const guid = this.data.treatmentPlans[index].treatmentPlanGuid;
      this.data.treatmentPlans.splice(index, 1);
      this.data.treatmentPlans = [...this.data.treatmentPlans];
      delete this.validationMap[guid];
  }

  public openRemoveTreatmentPlansConfirmationModal() {
    this.popupLoader.popupModal(ConfirmModalComponent,
      new ConfirmModalLoaderPayload(
        `Delete ${this.selectedTreatmentsAmount} ${this.selectedTreatmentsAmount > 1 ? 'Treatment Plans?' : 'Treatment Plan?'}`,
        `${this.selectedTreatmentsAmount} ${this.selectedTreatmentsAmount > 1 ? 'Treatment Plans are' : 'Treatment Plan is'} about to be deleted, would you want to continue?`,
        'Delete',
        'Cancel'
      ),
      ModalTypes.ALERT
    ).pipe(
      map(res => res.payload)
    ).subscribe((res: modalResponse) => {
      switch (res) {
        case modalResponse.approve:
          this.removeTreatmentPlans();
        default:
          return;
      }
    });
  }

  public removeTreatmentPlans(): void {
    this.selectedTreatments.forEach((selected: boolean, index: number) => {
      if (selected) {
        const guid = this.data.treatmentPlans[index].treatmentPlanGuid;
        delete this.validationMap[guid];
      }
    });
    this.data.treatmentPlans = [...this.data.treatmentPlans.filter((treatment: TreatmentPlanData, index: number) => !this.isTreatmentSelected(index))];
    this.selectedTreatments = this.data.treatmentPlans.map(() => false);
  }

  public orderChanged($event) {
      // this.data.treatmentPlans = [...$event];
  }

  public updateItemValue(index, form): void {
      const originalPrimary = this.data.treatmentPlans[index].primary;

      let relatedEntity;
      for (const entity of this.revisedEntitiesRevisionsData) {
          relatedEntity = entity.revisions.find(rev => rev.code === form.treatmentPlan.revisionId);
          if (relatedEntity) {
              break;
          }
      }
      form.treatmentPlan.curationDataId = relatedEntity ? relatedEntity.curationDataId : form.treatmentPlan.curationDataId;
      Object.assign(this.data.treatmentPlans[index], form);
      this.data.treatmentPlans[index].primary = originalPrimary;

      this.nodePanelConfigurationService.storeNodeConfigurationData(this.data, this.payload.readOnlyMode);
  }

  public setPrimary($event, index: number): void {
      for (let i = 0; i < this.data.treatmentPlans.length; i++) {
          if (i === index) {
              this.data.treatmentPlans[i].primary = !this.isPrimary(index);
          } else {
              this.data.treatmentPlans[i].primary = false;
          }
      }
  }

  public isPrimary(index: number): boolean {
      return this.data.treatmentPlans[index].primary;
  }

  public expandCollapsePanel($event, index) {
      if (this.panelsState.get(index) === PANEL_MODES.COLLAPSED) {
        this.panelsState.set(index, PANEL_MODES.EXPANDED);
      } else {
        this.panelsState.set(index, PANEL_MODES.COLLAPSED);
      }
  }

  noteChanged(evt) {
    this.data.note = evt;
    this.nodePanelConfigurationService.storeNodeConfigurationData(this.data, this.payload.readOnlyMode);
  }

  public cancel() {
      this.callback(undefined);
  }

  public save() {
      console.log('Treatment Plan Node Save: ', this.data);
      this.callback(this.data);
  }

  public buildTreatmentPlanUrl(data: any) {
    return this.selectorUrlBuilderService.getSelectorUrl(data.treatmentPlan, 'treatmentplan');
  }

  public navigateToTreatmentPlanUrl(data: any) {
    const treatmentPlanLink = this.selectorUrlBuilderService.getSelectorUrl(data.treatmentPlan, 'treatmentplan');
    let windowReference = window.open(treatmentPlanLink, '_blank');
    windowReference.location.href = treatmentPlanLink;
    windowReference.focus();
  }

  public isTreatmentPlanSelectorValid(): boolean {
      return !this.data.treatmentPlans || this.data.treatmentPlans.filter(treatmentPlan => this.validationMap[treatmentPlan.treatmentPlanGuid] === false).length === 0;
  }

  public validationEvent(index, valid) {
      if (typeof valid === 'boolean') {
          this.validationMap[this.data.treatmentPlans[index].treatmentPlanGuid] = this.isIDandNameExistedForTreatment(this.data.treatmentPlans[index]) && valid;
      }
  }

  private isIDandNameExistedForTreatment(treatmentPlanObject: TreatmentPlanData): boolean {
      return !!treatmentPlanObject.treatmentPlan && !!treatmentPlanObject.treatmentPlan.treatmentPlanId && !!treatmentPlanObject.treatmentPlan.display;
  }

  public collapseAllTreatmentPlans(event): void {
    this.setSelectedPanelsToPanelMode((this.panelExpandMode === PANEL_MODES.COLLAPSED) ? PANEL_MODES.EXPANDED : PANEL_MODES.COLLAPSED);
  }

  private setSelectedPanelsToPanelMode(panelMode: PANEL_MODES) {
    Array.from(this.panelsState.keys())
    .forEach(treatmentIndex => {
      if (this.isTreatmentSelected(treatmentIndex)) {
        this.panelsState.set(treatmentIndex, panelMode);
      }
    });
  }

  public onSelectAllClick(checked: boolean): void {
    this.selectedTreatments = this.data.treatmentPlans.map(() => checked ? true : false);
  }

  private scrollToBottom(): void {
    try {
      this.treatmentsScrollContainer.nativeElement.scrollTop = this.treatmentsScrollContainer.nativeElement.scrollHeight;
    } catch (err) { }
  }
}
